import {
  BaseQueryFn, createApi, fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { selectAuthToken } from '../../redux/reducers/authReducer';
import {
  extractProductKey,
  missingKeyProduct,
  replaceProductKey,
} from '../../helper/requests/productKeyUrl';

export type ResponseError = {
  status: number | string,
  data: string,
  description?: string,
  meta?: {
    url?: string,
    method?: string,
    args: any,
    retries?: number,
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GetState = { getState: () => any };
type PrepareHeadersFunc = (headers: Headers, options: GetState) => Headers;

export interface GenericResponse<DataType, MessageType = string> {
  status: number,
  code: number,
  type: number,
  title: string,
  message: MessageType,
  data: DataType,
}

const prepareHeaders: PrepareHeadersFunc = (headers: Headers, { getState }: GetState): Headers => {
  const token = selectAuthToken(getState());
  if (token !== '') {
    headers.set('Authorization', token);
  }

  return headers;
};

// Pausas antes de cada nova tentativa após falha de rede (2 retries no máximo).
export const NETWORK_RETRY_DELAYS_MS = [600, 1500];

const sleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

// "Failed to fetch" (FETCH_ERROR) é a requisição que nem chegou a ter resposta. O
// PARSING_ERROR com o mesmo texto é a conexão cortada no meio do corpo. Os dois são
// transitórios; um 4xx/5xx com corpo NÃO entra aqui — isso é resposta do servidor.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTransientNetworkFailure = (result: any): boolean => {
  const status = result?.error?.status;
  if (status === 'FETCH_ERROR') return true;
  return status === 'PARSING_ERROR' && /failed to fetch/i.test(String(result.error.error ?? ''));
};

// Transporte puro (sem normalização) — também usado no retry 1× da recuperação
// silenciosa de product_key: o retry chama o transporte direto, nunca o
// wrapper, então loop de retry é estruturalmente impossível (issue #1841).
//
// Queries repetem sozinhas em falha de rede transitória (erro de produção: rajadas de
// `Failed to fetch` levavam o usuário à tela de erro por um soluço de conexão). Mutations
// NUNCA repetem: a requisição pode ter chegado ao servidor e repetir duplicaria a gravação.
const transport: BaseQueryFn = async (args, api, extraOptions) => {
  const send = () => fetchBaseQuery({
    baseUrl: process.env.REACT_APP_AMPERE_API_URL,
    prepareHeaders,
  })(args, api, extraOptions);

  let result = await send();
  if (api.type !== 'query') return result;

  let retries = 0;
  while (
    retries < NETWORK_RETRY_DELAYS_MS.length
    && isTransientNetworkFailure(result)
    && !api.signal?.aborted
  ) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(NETWORK_RETRY_DELAYS_MS[retries]);
    if (api.signal?.aborted) break;
    retries += 1;
    // eslint-disable-next-line no-await-in-loop
    result = await send();
  }

  // Só anota quando houve retry, para não alterar o formato das respostas normais.
  return retries > 0 ? { ...result, meta: { ...result.meta, retries } } : result;
};

// Normalização (comportamento pré-existente, inalterado): erro de transporte
// OU body com code !== 200 viram ResponseError { status, data: message }.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeResult = (result: any, args: any) => {
  if (result.error) {
    const error: ResponseError = {
      status: result.error.status,
      data: (result.error as any).error,
      meta: {
        url: result.meta?.request?.url,
        method: result.meta?.request?.method,
        args: args.body,
        ...(result.meta?.retries ? { retries: result.meta.retries } : {}),
      },
    };

    return { error };
  }

  const data = result.data as { code: number, message: string, description?: string };
  if (!data) {
    return result;
  }

  if (data.code !== 200) {
    const error: ResponseError = {
      status: data.code,
      data: data.message,
      description: data.description,
      meta: {
        url: result.meta?.request?.url,
        method: result.meta?.request?.method,
        args: args.body,
      },
    };

    return { error };
  }

  return result;
};

const isProductKey403 = (error?: ResponseError) => (
  error?.status === 403 && error?.data !== 'JWT Inválido'
);

// F3: a sentinela `__missing__<produto>` (getProductKey sem chave no store)
// NUNCA vai para a rede — vira chave vazia, exatamente o que o app enviava
// antes do fix. O produto que ela carregava fica guardado aqui e serve de
// marcador na recuperação pós-403. Comportamento fail-open: a request sempre
// sai, e a chave só é buscada quando o servidor de fato recusa.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const semSentinela = (args: any): { enviados: any, produtoAusente: string | null } => {
  const produtoAusente = missingKeyProduct(extractProductKey(args));
  if (!produtoAusente) return { enviados: args, produtoAusente: null };
  const enviados = typeof args === 'string'
    ? replaceProductKey(args, '')
    : replaceProductKey(args as { url: string }, '');
  return { enviados, produtoAusente };
};

// F1: pós-403 de product_key, descobre o produto (sentinela que veio na URL OU
// reverse lookup da chave velha no slice), renova 1× (mutex por produto) e
// devolve os args com a URL reescrita — ou null quando não há o que repetir
// (rota sem product_key, produto desconhecido, renovação negada, chave igual).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const recoveredArgs = async (args: any, produtoAusente: string | null) => {
  const staleKey = extractProductKey(args);
  if (staleKey === null) return null;
  const recovery = await import('services/api/productKeyRecovery');
  const product = produtoAusente ?? recovery.productForStaleKey(staleKey);
  if (!product) return null;
  const freshKey = await recovery.ensureFreshProductKey(product, staleKey || null);
  if (!freshKey || freshKey === staleKey) return null;
  return typeof args === 'string'
    ? replaceProductKey(args, freshKey)
    : replaceProductKey(args as { url: string }, freshKey);
};

export const baseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const { enviados, produtoAusente } = semSentinela(args);

  const result = normalizeResult(await transport(enviados, api, extraOptions), enviados);
  if (!('error' in result) || !isProductKey403(result.error as ResponseError)) return result;

  const retryArgs = await recoveredArgs(enviados, produtoAusente);
  if (!retryArgs) return result; // fallback: /forbidden via handleAtuthorizationErrors

  return normalizeResult(await transport(retryArgs, api, extraOptions), retryArgs);
};

// Mesmo fluxo de recuperação para os endpoints com queryFn custom — aqui o
// 403 vem no BODY (sem normalização), então a detecção é por code/message.
export const rawBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const { enviados, produtoAusente } = semSentinela(args);

  const result = await transport(enviados, api, extraOptions);
  const body = result.data as { code?: number, message?: string } | undefined;
  if (!(body?.code === 403 && body?.message !== 'JWT Inválido')) return result;

  const retryArgs = await recoveredArgs(enviados, produtoAusente);
  if (!retryArgs) return result;
  return transport(retryArgs, api, extraOptions);
};

const baseApi = createApi({
  baseQuery,
  tagTypes: [
    'User',
    'Company',
    'Contract',
    'Videos',
    'ObservedDataPreferences',
    'PntPreferences',
    'ComparatorPreferences',
    'Study',
    'PastSimulation',
    'FaPreferences',
    'PersonalizedStudy',
    'PersonalizedStudyScenarios',
    'SeasonalForecast',
    'SeasonalForecastPreferences',
    'SeasonalComparatorPreferences',
    'AgroLocations',
    'AgroGroups',
    'FieldsLocations',
    'FieldsGroups',
    'TrackingForecastDetailedViewPreferences',
    'AgroTrackingForecastDetailedViewPreferences',
    'AnomalyMap',
    'IndicesClimaticos',
    'FluxAboutDocument',
    'WindxPainelOptions',
    'WindxEntidades',
    'WindxModelosDisponiveis',
    'WindxChartDataSH',
    'WindxChartDataSM',
    'WindxComparativoSM',
    'WindxPreferencias',
    'WindxSimulacoesAntigas',
    'WindxUsinasEolicas',
    'RelatorioAprovacao',
    'RelatorioAprovacaoCalendario',
  ],
  endpoints: () => ({}),
});

export { prepareHeaders };
export default baseApi;
